import { size, filter, forEach, extend, keyBy } from "lodash";
import React from "react";
import PropTypes from "prop-types";
import location from "@/services/location";
import { Parameter, createParameter } from "@/services/parameters";
import ParameterApplyButton from "@/components/ParameterApplyButton";
import EditParameterSettingsDialog from "@/components/EditParameterSettingsDialog";
import DashboardSortableParamRow from "./DashboardSortableParamRow";
import { dashGlobalHideFragmentsFromLocation, dashboardParamDragId, isDashboardGlobalParameterRowVisible } from "./dashboard-param-dnd-utils";

import "@/components/Parameters.less";

function updateUrl(parameters) {
  const params = extend({}, location.search);
  parameters.forEach(param => {
    extend(params, param.toUrlParams());
  });
  location.setSearch(params, true);
}

/**
 * Global-parameter list for dashboards in edit mode (dnd-kit sortable handled by SortableContext parent).
 */
export default class DashboardGlobalParameters extends React.Component {
  static propTypes = {
    barId: PropTypes.string.isRequired,
    parameters: PropTypes.arrayOf(PropTypes.instanceOf(Parameter)),
    editable: PropTypes.bool,
    dragEnabled: PropTypes.bool,
    disableUrlUpdate: PropTypes.bool,
    onValuesChange: PropTypes.func,
    onPendingValuesChange: PropTypes.func,
    onParametersEdit: PropTypes.func,
    parameterTabAssignmentOptions: PropTypes.shape({
      tabs: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        })
      ),
      assignments: PropTypes.object, // eslint-disable-line react/forbid-prop-types
      fallbackTabId: PropTypes.string,
      onAssignmentChange: PropTypes.func,
    }),
  };

  static defaultProps = {
    parameters: [],
    editable: true,
    dragEnabled: true,
    disableUrlUpdate: false,
    onValuesChange: () => {},
    onPendingValuesChange: () => {},
    onParametersEdit: () => {},
    parameterTabAssignmentOptions: null,
  };

  constructor(props) {
    super(props);
    const { parameters, disableUrlUpdate } = props;
    this.state = { parameters };
    if (!disableUrlUpdate) {
      updateUrl(parameters);
    }
    this.hideFragments = dashGlobalHideFragmentsFromLocation();
  }

  componentDidUpdate = prevProps => {
    const { parameters, disableUrlUpdate } = this.props;
    const parametersChanged = prevProps.parameters !== parameters;
    const disableUrlUpdateChanged = prevProps.disableUrlUpdate !== disableUrlUpdate;
    if (parametersChanged) {
      const prevByName = keyBy(this.state.parameters, "name");
      const merged = parameters.map(p => {
        const prev = prevByName[p.name];
        if (prev && prev.hasPendingValue) {
          p.setPendingValue(prev.pendingValue);
        }
        return p;
      });
      this.setState({ parameters: merged });
    }
    if ((parametersChanged || disableUrlUpdateChanged) && !disableUrlUpdate) {
      updateUrl(parameters);
    }
  };

  handleKeyDown = e => {
    if (e.keyCode === 13 && (e.ctrlKey || e.metaKey || e.altKey)) {
      e.stopPropagation();
      this.applyChanges();
    }
  };

  setPendingValue = (param, value, isDirty) => {
    const { onPendingValuesChange } = this.props;
    this.setState(({ parameters }) => {
      if (isDirty) {
        param.setPendingValue(value);
      } else {
        param.clearPendingValue();
      }
      onPendingValuesChange();
      return { parameters };
    });
  };

  applyChanges = () => {
    const { onValuesChange, disableUrlUpdate } = this.props;
    this.setState(({ parameters }) => {
      const parametersWithPendingValues = parameters.filter(p => p.hasPendingValue);
      forEach(parameters, p => p.applyPendingValue());
      if (!disableUrlUpdate) {
        updateUrl(parameters);
      }
      onValuesChange(parametersWithPendingValues);
      return { parameters };
    });
  };

  showParameterSettings = (parameter, index) => {
    const { onParametersEdit } = this.props;
    EditParameterSettingsDialog.showModal({ parameter }).onClose(updated => {
      this.setState(({ parameters }) => {
        const updatedParameter = extend(parameter, updated);
        parameters[index] = createParameter(updatedParameter, updatedParameter.parentQueryId);
        onParametersEdit(parameters);
        return { parameters };
      });
    });
  };

  shouldRender(param) {
    return isDashboardGlobalParameterRowVisible(param, this.hideFragments);
  }

  render() {
    const { parameters } = this.state;
    const { barId, dragEnabled, editable, parameterTabAssignmentOptions } = this.props;
    const dirtyParamCount = size(filter(parameters, "hasPendingValue"));
    const renderedRows = parameters.map((param, index) => {
      if (!this.shouldRender(param)) {
        return null;
      }
      const dragId = dashboardParamDragId(barId, param.name);
      return (
        <DashboardSortableParamRow
          key={param.name}
          dragId={dragId}
          dragEnabled={dragEnabled}
          param={param}
          paramIndex={index}
          editable={editable}
          parameterTabAssignmentOptions={parameterTabAssignmentOptions}
          onSetPendingValue={this.setPendingValue}
          onShowParameterSettings={this.showParameterSettings}
        />
      );
    });

    return (
      <div
        className="parameter-container dashboard-global-params-parameter-container"
        onKeyDown={dirtyParamCount ? this.handleKeyDown : null}>
        {renderedRows}
        <ParameterApplyButton onClick={this.applyChanges} paramCount={dirtyParamCount} />
      </div>
    );
  }
}
